import {
  Box,
  Button,
  Card,
  Loader,
  Modal,
  SimpleGrid,
  ScrollArea,
  Title,
  Flex,
  Group,
  Text,
  Tooltip,
} from '@mantine/core';
import { usePatient } from '@hooks/usePatient';
import { useDisclosure } from '@mantine/hooks';
import PatientNotesPreview from '../patient-notes/PatientNotesPreview';
import {
  FC,
  PropsWithChildren,
  cloneElement,
  isValidElement,
  useRef,
  useState,
} from 'react';
import {
  IconEye,
  IconPencil,
  IconExclamationCircle,
} from '@tabler/icons-react';
import MedicalHistory from '../medical-history/MedicalHistory';
import { useTranslation } from 'react-i18next';
import { GrowthCharts } from '../growth-charts/GrowthCharts';
import Examination from '../examination/Examination';
import { useCurrentEncounter } from '@/hooks/useCurrentEncounter';
// import { PatientCanvas } from '../patient-notes/PatientCanvas';
import ConfirmCloseDialog from '@/components/ConfirmCloseDialog';
import Pedigree from '../pedigree/Pedigree';
import { PsychomotorProgress } from './PsychomotorProgress';

// -----------------------------------------------------------------------------
export type SummaryTabMode = 'view' | 'edit';

// -----------------------------------------------------------------------------
export interface SummaryTabBlockChildProps {
  minimizedView?: boolean;
  onCloseModal?: () => void;
  setCanCloseRef?: (canClose: boolean) => void;
  mode?: SummaryTabMode;
}

// -----------------------------------------------------------------------------
const SummaryTabBlock: FC<
  PropsWithChildren & {
    title?: string;
    disableButtons?: boolean;
    disableEdit?: boolean;
    disableView?: boolean;
    size?: string;
    requiredEncounter?: boolean;
  }
> = ({
  children,
  title = 'View',
  disableButtons = false,
  disableEdit = false,
  disableView = false,
  size = 'xl',
  requiredEncounter = true,
}) => {
  const [modalShown, { close, toggle }] = useDisclosure(false);
  const [modalMode, setModalMode] = useState<SummaryTabMode>('view');
  const patient = usePatient();
  const { t } = useTranslation(['patient-pages', 'common', 'encounters']);
  const { currentEncounter } = useCurrentEncounter(patient);

  const [
    warningModalState,
    { open: openWarningModal, close: closeWarningModal },
  ] = useDisclosure();

  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const canCloseRef = useRef(true);

  const handleViewClick = () => {
    setModalMode('view');
    toggle();
  };

  const handleEditClick = () => {
    if (requiredEncounter && !currentEncounter) {
      openWarningModal();
      return;
    }
    setModalMode('edit');
    toggle();
  };

  const childrenWithProps = isValidElement(children)
    ? cloneElement(children as React.ReactElement<SummaryTabBlockChildProps>, {
        minimizedView: !modalShown || disableButtons,
        onCloseModal: () => {
          canCloseRef.current = true;
          close();
        },
        setCanCloseRef: (canClose: boolean) => {
          canCloseRef.current = canClose;
        },
        mode: modalMode,
      })
    : children;

  return (
    <>
      <Card shadow="sm" padding="sm" radius="md" h={300} miw={380} withBorder>
        <Card.Section withBorder inheritPadding py="xs" mb="xs">
          <Flex justify="space-between" align="center">
            <Title order={4}>{title}</Title>
            <Group gap="xs">
              {!disableButtons && (
                <>
                  {!disableView && (
                    <Tooltip
                      label={t('buttons.view', {
                        ns: 'common',
                        defaultValue: 'View',
                      })}
                    >
                      <Button
                        variant="subtle"
                        size="xs"
                        onClick={handleViewClick}
                      >
                        <IconEye size={14} />
                      </Button>
                    </Tooltip>
                  )}
                  {!disableEdit && (
                    <Tooltip
                      label={t('buttons.edit', {
                        ns: 'common',
                        defaultValue: 'Edit',
                      })}
                    >
                      <Button
                        variant="light"
                        size="xs"
                        onClick={handleEditClick}
                      >
                        <IconPencil size={14} />
                      </Button>
                    </Tooltip>
                  )}
                </>
              )}
            </Group>
          </Flex>
        </Card.Section>

        {!modalShown || disableButtons ? (
          <ScrollArea w="100%" h={230} type="auto">
            {childrenWithProps}
          </ScrollArea>
        ) : (
          <Modal
            opened={modalShown}
            onClose={() => {
              if (canCloseRef.current) {
                close();
              } else {
                setShowConfirmClose(true);
              }
            }}
            closeOnEscape={modalMode !== 'edit'}
            closeOnClickOutside={modalMode !== 'edit'}
            size={size}
            title={<Title order={3}>{title}</Title>}
          >
            {childrenWithProps}
          </Modal>
        )}
      </Card>

      {/* Warning Modal */}
      <Modal
        opened={warningModalState}
        onClose={closeWarningModal}
        title={
          <Group gap="xs">
            <IconExclamationCircle size={20} color="orange" />
            <Text>
              {t('warning', { ns: 'common', defaultValue: 'Warning' })}
            </Text>
          </Group>
        }
        size="sm"
      >
        <Text mb="md">
          {t('editRequiresEncounter', {
            ns: 'encounters',
            defaultValue:
              'You need to have an encounter in progress to edit this information.',
          })}
        </Text>
        <Group justify="flex-end">
          <Button variant="light" onClick={closeWarningModal}>
            {t('buttons.close', { ns: 'common', defaultValue: 'Close' })}
          </Button>
        </Group>
      </Modal>

      {/* Close Confirmation Dialog */}
      <ConfirmCloseDialog
        opened={showConfirmClose}
        onCancel={() => setShowConfirmClose(false)}
        onConfirm={() => {
          setShowConfirmClose(false);
          close();
        }}
      />
    </>
  );
};

// -----------------------------------------------------------------------------
export const SummaryTab: FC = () => {
  const patient = usePatient();
  const { t } = useTranslation(['patient-pages']);

  if (!patient) {
    return <Loader />;
  }

  // ---------------------------------------------------------------------------
  return (
    <Box w={'100%'} h="100%">
      <SimpleGrid
        type="container"
        cols={{ base: 1, '800px': 2, '1400px': 3 }}
        spacing="xl"
        mt="md"
        p="md"
        verticalSpacing="xl"
      >
        <SummaryTabBlock
          title={t('summaryPage.titles.familyPedigree', {
            ns: 'patient-pages',
          })}
          size="90vw"
        >
          <Pedigree />
        </SummaryTabBlock>
        <SummaryTabBlock
          title={t('summaryPage.titles.medicalHistory', {
            ns: 'patient-pages',
          })}
          size="100%"
        >
          <MedicalHistory />
        </SummaryTabBlock>
        <SummaryTabBlock
          title={t('summaryPage.titles.patientNotes', {
            ns: 'patient-pages',
          })}
          disableButtons
        >
          <PatientNotesPreview />
        </SummaryTabBlock>
        <SummaryTabBlock
          title={t('summaryPage.titles.growthCharts', {
            ns: 'patient-pages',
          })}
          disableEdit
        >
          <GrowthCharts showTitle={false} />
        </SummaryTabBlock>
        <SummaryTabBlock
          title={t('psychomotorDevelopment.title', {
            ns: 'patient-pages',
            defaultValue: 'Psychomotor Development',
          })}
          disableEdit
        >
          <PsychomotorProgress />
        </SummaryTabBlock>
        <SummaryTabBlock
          title={t('summaryPage.titles.examination', {
            ns: 'patient-pages',
          })}
          disableButtons
        >
          <Examination />
        </SummaryTabBlock>{' '}
        {/* <SummaryTabBlock
          title={t('summaryPage.titles.patientCanvas', {
            ns: 'patient-pages',
          })}
          requiredEncounter={false}
        >
          <PatientCanvas />
        </SummaryTabBlock> */}
      </SimpleGrid>
    </Box>
  );
};
